/**
 * SupRadioPanel — Supervisor Radios panel.
 *
 * Displays:
 *   - All radios from the `supRadios` store slice (e.g. SGT-100, SGT-101)
 *   - PLUS the three security-supervisor radios (95, 97, 99 → S-01, S-02, S-03)
 *     pulled visually from the `otherRadios` store slice.
 *
 * The underlying data storage for 95/97/99 stays in `otherRadios` so the
 * D1 sync model and FOM compatibility are unaffected — this is a display-
 * only regrouping. When the user toggles OOS / edits assignee / etc. on
 * those three, writes are routed back to the correct store slice.
 */
import { useDesktopStore } from '../../stores/desktop'
import type { RadioSlot } from '../../stores/desktop'
import RadioSection from './RadioSection'

// Security-supervisor radios live in the `otherRadios` slice but display here.
const SECURITY_SUP_IDS = ['95', '97', '99']

export default function SupRadioPanel() {
  const { supRadios, setSupRadios, otherRadios, setOtherRadios } = useDesktopStore()

  // Pull the three security-supervisor radios from otherRadios (preserving order S-01, S-02, S-03)
  const securitySupRadios = SECURITY_SUP_IDS
    .map(id => otherRadios.find(r => r.id === id))
    .filter((r): r is RadioSlot => !!r)

  // Merge for display — supRadios first (SGT-100/101), then security supervisors
  const displayed: RadioSlot[] = [...supRadios, ...securitySupRadios]

  // Split writes back to the correct store slice based on radio id.
  // Radios with ids in SECURITY_SUP_IDS go back to otherRadios;
  // everything else (including user-added radios) stays in supRadios.
  function handleSet(next: RadioSlot[]) {
    const nextSup = next.filter(r => !SECURITY_SUP_IDS.includes(r.id))
    const nextSecSup = next.filter(r => SECURITY_SUP_IDS.includes(r.id))
    const nextOther = [
      ...otherRadios.filter(r => !SECURITY_SUP_IDS.includes(r.id)),
      ...nextSecSup,
    ]
    setSupRadios(nextSup)
    setOtherRadios(nextOther)
  }

  return (
    <RadioSection
      radios={displayed}
      setRadios={handleSet}
      title="Supervisor Radios"
      naType="Sup Radio"
      officerVariant
    />
  )
}
